import { Archive, Clock, FileCheck, FileText, Send } from 'lucide-react';

// ─── Encounter Type ─────────────────────────────────────────────

export const ENCOUNTER_TYPES = ['initial', 'follow-up', 'urgent', 'telehealth'] as const;

export const encounterTypeLabels: Record<string, string> = {
  urgent: 'Urgent Visit',
  initial: 'Initial Visit',
  'follow-up': 'Follow-up',
  telehealth: 'Telehealth',
};

export const encounterTypeColors: Record<string, string> = {
  urgent: 'bg-red-100 text-red-800',
  initial: 'bg-blue-100 text-blue-800',
  'follow-up': 'bg-green-100 text-green-800',
  telehealth: 'bg-purple-100 text-purple-800',
};

export const encounterTypeBadgeStyles: Record<string, string> = {
  urgent: 'bg-red-50 text-red-700 border-red-200',
  initial: 'bg-blue-50 text-blue-700 border-blue-200',
  telehealth: 'bg-violet-50 text-violet-700 border-violet-200',
  'follow-up': 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const encounterTypeToggleColors: Record<string, string> = {
  urgent: 'data-[state=on]:bg-red-100 data-[state=on]:text-red-800',
  initial: 'data-[state=on]:bg-blue-100 data-[state=on]:text-blue-800',
  'follow-up': 'data-[state=on]:bg-green-100 data-[state=on]:text-green-800',
  telehealth: 'data-[state=on]:bg-purple-100 data-[state=on]:text-purple-800',
};

export const encounterTypeTimelineDotColors: Record<string, string> = {
  urgent: 'bg-red-500',
  initial: 'bg-blue-500',
  telehealth: 'bg-purple-500',
  'follow-up': 'bg-green-500',
};

export const encounterTypeStatColors: Record<string, string> = {
  urgent: 'bg-red-50 text-red-600',
  initial: 'bg-blue-50 text-blue-600',
  'follow-up': 'bg-green-50 text-green-600',
  telehealth: 'bg-purple-50 text-purple-600',
};

// ─── Encounter Status ───────────────────────────────────────────

export const encounterStatusColors: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  submitted: 'bg-blue-100 text-blue-800',
  complete: 'bg-green-100 text-green-800',
};

// ─── Document Status ────────────────────────────────────────────

export const documentStatusColors: Record<string, string> = {
  exported: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-100 text-gray-800',
  draft: 'bg-yellow-100 text-yellow-800',
  generating: 'bg-blue-100 text-blue-800',
  finalized: 'bg-green-100 text-green-800',
};

export const documentStatusBadgeStyles: Record<string, string> = {
  exported: 'bg-sky-50 text-sky-700 border-sky-200',
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  generating: 'bg-blue-50 text-blue-700 border-blue-200',
  archived: 'bg-slate-50 text-slate-600 border-slate-200',
  'in-progress': 'bg-blue-50 text-blue-700 border-blue-200',
  finalized: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const documentStatusIcons: Record<string, typeof FileText> = {
  exported: Send,
  draft: FileText,
  archived: Archive,
  finalized: FileCheck,
};

// ─── Action Status ──────────────────────────────────────────────

export const actionStatusConfig: Record<string, { bg: string }> = {
  failed: { bg: 'bg-red-50 text-red-600' },
  running: { bg: 'bg-blue-50 text-blue-600' },
  pending: { bg: 'bg-amber-50 text-amber-600' },
  cancelled: { bg: 'bg-slate-50 text-slate-500' },
  completed: { bg: 'bg-emerald-50 text-emerald-600' },
};

// ─── Timeline Item Types ────────────────────────────────────────

export const timelineDotColors: Record<string, string> = {
  lab: 'bg-purple-500',
  generated: 'bg-blue-500',
  uploaded: 'bg-amber-500',
  encounter: 'bg-green-500',
  prescription: 'bg-rose-500',
};

export const timelineKindLabels: Record<string, string> = {
  lab: 'Lab',
  encounter: 'Encounter',
  prescription: 'Prescription',
  uploaded: 'Uploaded Document',
  generated: 'Generated Document',
};

// ─── Uploaded Document Type ─────────────────────────────────────

export const UPLOADED_DOCUMENT_TYPES = [
  { value: 'referral', label: 'Referral' },
  { value: 'lab_result', label: 'Lab Result' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'medical_record', label: 'Medical Record' },
  { value: 'imaging', label: 'Imaging' },
  { value: 'other', label: 'Other' },
] as const;

export const uploadedDocumentTypeLabels: Record<string, string> = {
  other: 'Other',
  imaging: 'Imaging',
  referral: 'Referral',
  lab_result: 'Lab Result',
  prescription: 'Prescription',
  medical_record: 'Medical Record',
};

// ─── Extraction Status ──────────────────────────────────────────

export const extractionStatusColors: Record<string, string> = {
  failed: 'bg-red-100 text-red-800',
  pending: 'bg-gray-100 text-gray-800',
  extracting: 'bg-blue-100 text-blue-800',
  extracted: 'bg-green-100 text-green-800',
  not_applicable: 'bg-gray-100 text-gray-600',
};

// ─── Accepted File Types ────────────────────────────────────────

export const ACCEPTED_FILE_TYPES = {
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

export const ACCEPTED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];

export const MAX_FILE_SIZE_MB = 25;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
